import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Sparkles, Hash, ScrollText, Star, MessageCircle, LogOut, User, Calendar, Globe, Heart, Crown, UserCog, Grid3X3, Hand, TrendingUp, CalendarCheck, Compass } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import ActivationPaywall from "@/components/ActivationPaywall";
import OfflineIndicator from "@/components/OfflineIndicator";
import VisitorCounter from "@/components/VisitorCounter";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const isGuest = !user; // user === false means not logged in

  const navItems = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: Star, testId: "nav-dashboard" },
    { to: "/numerology", label: t("nav.numerology"), icon: Hash, testId: "nav-numerology" },
    { to: "/lo-shu-grid", label: "Lo Shu", icon: Grid3X3, testId: "nav-lo-shu" },
    { to: "/name-numerology", label: t("nav.name"), icon: ScrollText, testId: "nav-name" },
    { to: "/birth-chart", label: t("nav.chart"), icon: Sparkles, testId: "nav-chart" },
    { to: "/matching", label: t("nav.matching"), icon: Heart, testId: "nav-matching" },
    { to: "/panchang", label: t("nav.panchang"), icon: Calendar, testId: "nav-panchang" },
    { to: "/muhurat", label: "Muhurat", icon: CalendarCheck, testId: "nav-muhurat" },
    { to: "/vastu", label: "Vastu", icon: Compass, testId: "nav-vastu" },
    { to: "/transits", label: "Transits", icon: TrendingUp, testId: "nav-transits" },
    { to: "/palm-reading", label: t("nav.palm"), icon: Hand, testId: "nav-palm" },
    { to: "/chat", label: t("nav.chat"), icon: MessageCircle, testId: "nav-chat" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      <div className="fixed inset-0 starfield opacity-40 pointer-events-none z-0" />

      <header className="sticky top-0 z-30 glass-strong border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="header-logo">
            <Sparkles className="w-6 h-6 text-gold group-hover:rotate-12 transition" />
            <span className="font-serif text-xl tracking-wide text-slate-50">Cosomic Sage</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition flex items-center gap-1.5 ${
                    isActive
                      ? "bg-amber-500/15 text-amber-400"
                      : "text-slate-300 hover:text-amber-300 hover:bg-white/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && !user.is_activated && user.role !== "admin" && (
              <Button
                size="sm"
                onClick={() => setPaywallOpen(true)}
                data-testid="header-activate-btn"
                className="hidden sm:inline-flex bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium gap-1.5 h-8 px-3"
              >
                <Crown className="w-3.5 h-3.5" /> Activate
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="lang-toggle"
                  className="p-2 rounded-full text-slate-300 hover:text-amber-300 hover:bg-white/5 transition flex items-center gap-1 text-xs uppercase tracking-wider"
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{lang.toUpperCase()}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-white/10 text-slate-200">
                <DropdownMenuItem
                  onClick={() => setLang("en")}
                  data-testid="lang-en"
                  className={lang === "en" ? "text-amber-300" : ""}
                >
                  English
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLang("hi")}
                  data-testid="lang-hi"
                  className={lang === "hi" ? "text-amber-300" : ""}
                >
                  हिन्दी
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/profile" data-testid="header-profile-link" className="p-2 rounded-full text-slate-300 hover:text-amber-300 hover:bg-white/5 transition" aria-label="Profile">
              <UserCog className="w-5 h-5" />
            </Link>
            {!isGuest ? (
              <>
                <div className="hidden md:flex items-center gap-2 text-sm text-slate-300">
                  <User className="w-4 h-4 text-gold" />
                  <span data-testid="user-name">{user?.name || user?.email}</span>
                </div>
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="p-2 rounded-full text-slate-300 hover:text-amber-300 hover:bg-white/5 transition"
                  aria-label={t("common.logout")}
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <Link to="/login">
                <Button size="sm" variant="outline" data-testid="header-signin-btn" className="rounded-full border-amber-400/40 text-amber-300 hover:bg-amber-500/10 h-8 px-3">
                  Sign in
                </Button>
              </Link>
            )}
          </div>
        </div>

        <nav className="md:hidden flex overflow-x-auto gap-1 px-2 pb-2 border-t border-white/5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-${item.testId}`}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  isActive ? "bg-amber-500/15 text-amber-400" : "text-slate-400"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 relative z-10 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      <footer className="relative z-10 border-t border-white/5 py-6 text-center text-xs text-slate-500 space-y-2">
        <VisitorCounter />
        <div>Cosomic Sage — Vedic + Western astro guidance with AI insights</div>
        <div className="flex items-center justify-center gap-4 text-slate-500">
          <Link to="/about" data-testid="footer-about" className="hover:text-amber-300">About</Link>
          <span className="text-slate-700">·</span>
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-amber-300">Privacy Policy</Link>
          <span className="text-slate-700">·</span>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-amber-300">Terms of Service</Link>
          <span className="text-slate-700">·</span>
          <a href="mailto:support@cosomicsage.com" data-testid="footer-contact" className="hover:text-amber-300">Contact</a>
        </div>
        <div className="text-amber-300/70 tracking-wider">Powered by <span className="font-medium">Gamya Consultancy and Electric Solution Pvt Ltd</span></div>
      </footer>

      <ActivationPaywall open={paywallOpen} onOpenChange={setPaywallOpen} feature="all premium tools" />
      <OfflineIndicator />
    </div>
  );
}
